import { Link } from "react-router-dom";
import type { Device } from "../types";
import { classifyStatus, formatRelative, getDeviceRouteId } from "../adapters";
import { estaSemNomeOperacional, nomeDeExibicao } from "../nomeDeExibicao";

interface DeviceCardProps {
  device: Device;
  detailsSearch?: string;
}

/**
 * Card de dispositivo na lista técnica (`/devices`). Reusa o estilo `.w`
 * do design novo, mas mostra metadados crus (mqttId, MAC, serial,
 * workspace) — esses são úteis só na rota técnica.
 */
export function DeviceCard({ device, detailsSearch = "" }: DeviceCardProps) {
  const routeId = getDeviceRouteId(device);
  const status = device.status ?? "unknown";
  const statusClass = classifyStatus(status);
  const badgeClass = statusClass === "online" ? "ok" : statusClass === "offline" ? "muted" : "diag";

  return (
    <Link
      to={`/devices/${encodeURIComponent(routeId)}${detailsSearch}`}
      className="w"
      style={{ textDecoration: "none", color: "inherit", minHeight: "auto" }}
    >
      <div className="w-hd">
        <div className="w-hd-l">
          {/* Sem `mono` quando ha nome operacional: fonte de codigo e para
              identificador de rede, nao para "Extrusora 2". */}
          <div
            className={estaSemNomeOperacional(device) ? "w-title mono" : "w-title"}
            title={String(device.mqttId)}
          >
            {nomeDeExibicao(device)}
          </div>
          <div className="w-sub">
            Última vez: {formatRelative(device.lastSeenAt)}
          </div>
        </div>
        <div className="w-hd-r">
          <span className={`badge ${badgeClass}`}>
            <span className={statusClass === "online" ? "led-anim" : "dot"} />
            {String(status)}
          </span>
        </div>
      </div>
      <div className="w-body col">
        <dl
          style={{
            margin: 0,
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            rowGap: 6,
            columnGap: 12,
            fontSize: 12,
            color: "var(--ink-700)",
          }}
        >
          <div>
            <dt
              style={{ fontSize: 10, color: "var(--ink-400)", textTransform: "uppercase", letterSpacing: "0.10em" }}
            >
              MAC
            </dt>
            <dd className="mono" style={{ margin: 0 }}>
              {device.mac ? String(device.mac) : "—"}
            </dd>
          </div>
          <div>
            <dt
              style={{ fontSize: 10, color: "var(--ink-400)", textTransform: "uppercase", letterSpacing: "0.10em" }}
            >
              Serial
            </dt>
            <dd className="mono" style={{ margin: 0 }}>
              {device.serialNumber ? String(device.serialNumber) : "—"}
            </dd>
          </div>
          <div>
            <dt
              style={{ fontSize: 10, color: "var(--ink-400)", textTransform: "uppercase", letterSpacing: "0.10em" }}
            >
              Primeiro visto
            </dt>
            <dd style={{ margin: 0 }}>{formatRelative(device.firstSeenAt)}</dd>
          </div>
          <div>
            <dt
              style={{ fontSize: 10, color: "var(--ink-400)", textTransform: "uppercase", letterSpacing: "0.10em" }}
            >
              Workspace
            </dt>
            <dd className="mono" style={{ margin: 0 }}>
              {device.currentWorkspaceId ? String(device.currentWorkspaceId) : "—"}
            </dd>
          </div>
        </dl>
      </div>
      <div className="w-ft">
        <span className="mono" title="device.id">
          id: {truncate(String(device.id), 18)}
        </span>
        <span aria-hidden>→</span>
      </div>
    </Link>
  );
}

function truncate(value: string, max: number): string {
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1)}…`;
}
